/**
 * ExperienceEventType -> rendered log-row text. The largest, highest-risk piece of logic
 * in the Experience Log feature (~30 event types, each with its own copy/interpolation
 * and null-guard for a missing denormalized task/message/automation) — pulled out of
 * index.tsx specifically so it's testable without importing that file's much heavier UI
 * graph. See toLogEvent.test.tsx for the safety net.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { ExperienceEvent } from '@dadohr/shared/domainTypes/event';
import {
  AutomationActionDenormalized,
  EnrollSourceType,
  ExperienceEventType,
  IAutomationDenormalized,
  IExperienceMessageDenormalized,
  IExperienceTemplateResource,
  ITaskDenormalized,
  SubTaskDenormalized,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { Routes } from '@dadohr/shared/domainTypes/routes';
import { IUser } from '@dadohr/shared/domainTypes/user';
import * as ENVS from '../../../../env.json';
import { assertNever } from 'assert-never';
import { capitalize, compact } from 'lodash';
import React from 'react';
import { LinkExternal } from '../../../../components/LinkExternal';
import { subTaskToText } from '../../../../components/SubTask';
import { UnsafeTFunction } from '../../../../i18next';
import { channelToString } from '../../../../services/channels';
import { joinWithCommaAndAnd } from '../../../../services/enumerate';
import { lightMdToJsx } from '../../../../services/formatter';
import { toMoment } from '../../../../services/time';
import { toFullName } from '../../../../services/user';
import { ActorDescriptor } from '../Actors';
import { AnchorField } from '../Anchors';
import { automationActionToText } from '../Builders/AutomationBuilder/actionHelpers';
import { ActionDefinition } from '../Builders/AutomationBuilder/types';
import { getItemDisplayName, getItemDisplayPair } from '../../helpers/adminName';

export const toLogDisplayMd = (item: Parameters<typeof getItemDisplayPair>[0]): string => {
  const { primary, secondary } = getItemDisplayPair(item);
  return secondary ? `**${primary}** *"${secondary}"*` : `**${primary}**`;
};

export type ExperienceEventRendererData = {
  tasksByTplId: { [taskTplId: string]: ITaskDenormalized };
  subTasksByTplId: { [subTaskTplId: string]: SubTaskDenormalized };
  automationsByTplId: { [automationTplId: string]: IAutomationDenormalized };
  automationActionsByTplId: {
    [automationActionTplId: string]: AutomationActionDenormalized;
  };
  messagesByTplId: { [msgTplId: string]: IExperienceMessageDenormalized };
  actorDescriptors: { [actorKey: string]: ActorDescriptor };
  usersById: { [userId: string]: Doc<IUser> };
  dateFormats: IOrganization['dateFormats'];
  tz?: string;
  anchor: AnchorField | null;
  automationActionDefinitions: ActionDefinition[];
  resourcesById: { [resourceId: string]: IExperienceTemplateResource };
  routes: Routes;
};

export const getActorLabel = (
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  actorKey: string,
  t: UnsafeTFunction
) => {
  return actorDescriptors[actorKey || '']?.label || t('Unknown');
};

export const toLogEvent = <T extends ExperienceEvent>(
  e: Doc<T>,
  data: ExperienceEventRendererData,
  t: UnsafeTFunction
  // TODO - need date format here
  // TODO - need anchor name
): { node: React.ReactNode; searchValue: string } | null => {
  const d = e.data;
  const task = data.tasksByTplId[(d as any).tTId || ''] || null;
  const subTask = data.subTasksByTplId[(d as any).sTId || ''] || null;
  const automation =
    data.automationsByTplId[(d as any).automationTplId] || null;
  const autAction =
    data.automationActionsByTplId[(d as any).acionTplId] || null;
  const message = data.messagesByTplId[(d as any).msgId] || null;
  const dateFormat = `${data.dateFormats.MMDDYYYY} HH:mm`;
  // MOVED events only show the from/to dates, not a time — in this tab (data.tz set)
  // that's the template timezone, date-only; the classic tab keeps the original
  // org-configured dateFormat (date + time, browser-local).
  const formatMovedDate = (ts: Parameters<typeof toMoment>[0]): string =>
    data.tz ? toMoment(ts).tz(data.tz).format('D MMM YYYY') : toMoment(ts).format(dateFormat);
  switch (d.type) {
    case ExperienceEventType.EXP_ENROLL:
      if (
        d.source.type === EnrollSourceType.ENROLLMENT_FORM_AUTOMATIC ||
        d.source.type === EnrollSourceType.ENROLLMENT_FORM_MANUAL
      ) {
        return {
          node: t('Enrolled via enrollment form'),
          searchValue: t('Enrolled via enrollment form')
        };
      }
      if (d.source.type === EnrollSourceType.MANUAL) {
        return {
          node: t('Enrolled manually'),
          searchValue: t('Enrolled manually')
        };
      }
      if (d.source.type === EnrollSourceType.ENROLL_AUTOMATION) {
        return {
          node: t('Enrolled via enroll automation'),
          searchValue: t('Enrolled via enroll automation')
        };
      }

      if (d.source.type === EnrollSourceType.IMPORT) {
        return {
          node: (
            <span>
              {t('Enrolled via')}{' '}
              <LinkExternal
                color="primary"
                href={data.routes.users.imports.details.url(d.source.importId)}
              >
                {t('user import')}
              </LinkExternal>
            </span>
          ),
          searchValue: t('Enrolled via user import')
        };
      }
      return {
        node: t('Enrolled in experience'),
        searchValue: t('Enrolled in experience')
      };
    case ExperienceEventType.EXP_START:
      return {
        node: t('Experience started'),
        searchValue: t('Experience started')
      };
    case ExperienceEventType.EXP_MESSAGE:
      if (!message) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('**{{ actor }}** received message {{ title }}', {
            title: toLogDisplayMd(message),
            actor: getActorLabel(data.actorDescriptors, message.actor, t)
          })
        ),
        searchValue: t('{{ actor }} received message {{ title }}', {
          title: [getItemDisplayName(message), message.adminName ? message.subject : ''].filter(Boolean).join(' '),
          actor: getActorLabel(data.actorDescriptors, message.actor, t)
        })
      };
    case ExperienceEventType.TASK_START:
      if (!task) {
        return null;
      }
      const actor = getActorLabel(data.actorDescriptors, task.actor, t);
      return {
        node: lightMdToJsx(
          `${t('Task for **{{ actor }}** started', { actor })}: ${toLogDisplayMd(task)}`
        ),
        searchValue: t('Task for {{ actor }} started {{ title }}', {
          actor,
          title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ')
        })
      };
    case ExperienceEventType.SUB_TASK_DONE:
      return null;
    case ExperienceEventType.SUB_TASK_UNDONE:
      if (!task || !subTask) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Uncompleted sub-task **{{ subTask }}** in {{ task }}', {
            subTask: subTaskToText(subTask, t),
            task: toLogDisplayMd(task)
          })
        ),
        searchValue: t('Uncompleted sub-task {{ subTask }} in {{ task }}', {
          subTask: subTaskToText(subTask, t),
          task: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ')
        })
      };
    case ExperienceEventType.TASK_SKIPPED:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(t('Skipped {{ title }}', { title: toLogDisplayMd(task) })),
        searchValue: t('Skipped {{ title }}', { title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ') })
      };
    case ExperienceEventType.TASK_UNSKIPPED:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Unskipped {{ title }}', { title: toLogDisplayMd(task) })
        ),
        searchValue: t('Unskipped {{ title }}', { title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ') })
      };
    case ExperienceEventType.SUB_TASK_SKIPPED:
      if (!task || !subTask) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Skipped sub-task **{{ subTask }}** in {{ task }}', {
            subTask: subTaskToText(subTask, t),
            task: toLogDisplayMd(task)
          })
        ),
        searchValue: t('Skipped sub-task {{ subTask }} in {{ task }}', {
          subTask: subTaskToText(subTask, t),
          task: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ')
        })
      };
    case ExperienceEventType.SUB_TASK_UNSKIPPED:
      if (!task || !subTask) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Unskipped sub-task **{{ subTask }}** in {{ task }}', {
            subTask: subTaskToText(subTask, t),
            task: toLogDisplayMd(task)
          })
        ),
        searchValue: t('Unskipped sub-task {{ subTask }} in {{ task }}', {
          subTask: subTaskToText(subTask, t),
          task: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ')
        })
      };
    case ExperienceEventType.MESSAGE_SKIPPED:
      if (!message) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Skipped {{ title }}', { title: toLogDisplayMd(message) })
        ),
        searchValue: t('Skipped {{ title }}', { title: [getItemDisplayName(message), message.adminName ? message.subject : ''].filter(Boolean).join(' ') })
      };
    case ExperienceEventType.MESSAGE_UNSKIPPED:
      if (!message) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Unskipped {{ title }}', { title: toLogDisplayMd(message) })
        ),
        searchValue: t('Unskipped {{ title }}', { title: [getItemDisplayName(message), message.adminName ? message.subject : ''].filter(Boolean).join(' ') })
      };
    case ExperienceEventType.AUTOMATION_RAN:
      if (!automation) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Ran **{{ title }}**', { title: automation.title })
        ),
        searchValue: t('Ran {{ title }}', { title: automation.title })
      };
    case ExperienceEventType.AUTOMATION_SKIPPED:
      if (!automation) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Skipped **{{ title }}**', { title: automation.title })
        ),
        searchValue: t('Skipped {{ title }}', { title: automation.title })
      };
    case ExperienceEventType.AUTOMATION_UNSKIPPED:
      if (!automation) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Unskipped **{{ title }}**', { title: automation.title })
        ),
        searchValue: t('Unskipped {{ title }}', { title: automation.title })
      };
    case ExperienceEventType.AUTOMATION_ACTION_SKIPPED:
      if (!autAction || !automation) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Skipped **{{ action }}** in **{{ automation }}**', {
            action: automationActionToText(
              autAction,
              data.automationActionDefinitions
            ),
            automation: automation.title
          })
        ),
        searchValue: t('Skipped {{ action }} in {{ automation }}', {
          action: automationActionToText(
            autAction,
            data.automationActionDefinitions
          ),
          automation: automation.title
        })
      };
    case ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED:
      if (!autAction || !automation) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Unskipped **{{ action }}** in **{{ automation }}**', {
            action: automationActionToText(
              autAction,
              data.automationActionDefinitions
            ),
            automation: automation.title
          })
        ),
        searchValue: t('Unskipped {{ action }} in {{ automation }}', {
          action: automationActionToText(
            autAction,
            data.automationActionDefinitions
          ),
          automation: automation.title
        })
      };
    case ExperienceEventType.TASK_REMINDER:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('**{{ actor }}** received reminder for task {{ title }}', {
            title: toLogDisplayMd(task),
            actor: getActorLabel(data.actorDescriptors, task.actor, t)
          })
        ),
        searchValue: t('{{ actor }} received reminder for task {{ title }}', {
          title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' '),
          actor: getActorLabel(data.actorDescriptors, task.actor, t)
        })
      };
    case ExperienceEventType.TASK_LATE_REMINDER:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t(
            '**{{ actor }}** received overdue reminder for task {{ title }}',
            {
              title: toLogDisplayMd(task),
              actor: getActorLabel(data.actorDescriptors, task.actor, t)
            }
          )
        ),
        searchValue: t(
          '{{ actor }} received overdue reminder for task {{ title }}',
          {
            title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' '),
            actor: getActorLabel(data.actorDescriptors, task.actor, t)
          }
        )
      };
    case ExperienceEventType.TASK_UNDONE:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Uncompleted {{ task }}', {
            task: toLogDisplayMd(task)
          })
        ),
        searchValue: t('Uncompleted {{ task }}', {
          task: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' ')
        })
      };
    case ExperienceEventType.TASK_DONE:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('**{{ actor }}** completed task {{ title }}', {
            title: toLogDisplayMd(task),
            actor: getActorLabel(data.actorDescriptors, task.actor, t)
          })
        ),
        searchValue: t('**{{ actor }}** completed task **{{ title }}**', {
          title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' '),
          actor: getActorLabel(data.actorDescriptors, task.actor, t)
        })
      };
    case ExperienceEventType.TASK_LATE:
      return null;
    case ExperienceEventType.EXP_DONE:
      return {
        node: t('Completed experience'),
        searchValue: t('Completed experience')
      };
    case ExperienceEventType.EXP_ARCHIVED:
      return {
        node: t('Experience archived'),
        searchValue: t('Experience archived')
      };
    case ExperienceEventType.SUPPORTING_ACTOR_CHANGED:
      const prevActor = d.prev ? data.usersById[d.prev] : null;
      const nextActor = data.usersById[d.next];
      const prevActorText = d.prev
        ? prevActor
          ? toFullName(prevActor.data)
          : `[${t('deleted user')}]`
        : t('no one');
      const nextActorText = nextActor
        ? toFullName(nextActor.data)
        : `[${t('deleted user')}]`;
      const actorChange = {
        actor: getActorLabel(data.actorDescriptors, d.actorKey, t),
        prev: prevActorText,
        next: nextActorText
      };
      // The template's actor was edited, and the change reached this instance.
      if (d.executedBy === 'template') {
        return {
          node: lightMdToJsx(
            t(
              'Changed **{{ actor }}** from {{ prev }} to {{ next }}, after **{{ actor }}** was updated on the overall experience',
              actorChange
            )
          ),
          searchValue: t(
            'Changed {{ actor }} from {{ prev }} to {{ next }}, after {{ actor }} was updated on the overall experience',
            actorChange
          )
        };
      }
      return {
        node: lightMdToJsx(
          t('Changed **{{ actor }}** from {{ prev }} to {{ next }}', actorChange)
        ),
        searchValue: t(
          'Changed {{ actor }} from {{ prev }} to {{ next }}',
          actorChange
        )
      };
    case ExperienceEventType.ANCHOR_CHANGED:
      return {
        node: lightMdToJsx(
          t('**{{ anchor }}** changed from {{ oldDate }} to {{ newDate }}', {
            anchor: capitalize(data.anchor?.shortLabel(t) || t('anchor')),
            oldDate: toMoment(d.prev).format(dateFormat),
            newDate: toMoment(d.next).format(dateFormat)
          })
        ),
        searchValue: t(
          '{{ anchor }} changed from {{ oldDate }} to {{ newDate }}',
          {
            anchor: capitalize(data.anchor?.shortLabel(t) || t('anchor')),
            oldDate: toMoment(d.prev).format(dateFormat),
            newDate: toMoment(d.next).format(dateFormat)
          }
        )
      };
    case ExperienceEventType.MESSAGE_MOVED: {
      if (!message) {
        return null;
      }
      const oldDate = formatMovedDate(d.prevStart);
      const newDate = formatMovedDate(d.nextStart);
      return {
        node: lightMdToJsx(
          t('Moved {{ title }} from sending on {{ oldDate }} to {{ newDate }}', {
            title: toLogDisplayMd(message),
            oldDate,
            newDate
          })
        ),
        searchValue: t(
          'Moved {{ title }} from sending on {{ oldDate }} to {{ newDate }}',
          {
            title: [getItemDisplayName(message), message.adminName ? message.subject : ''].filter(Boolean).join(' '),
            oldDate,
            newDate
          }
        )
      };
    }
    case ExperienceEventType.TASK_MOVED: {
      if (!task) {
        return null;
      }
      const oldDate = formatMovedDate(d.prevStart);
      const newDate = formatMovedDate(d.nextStart);
      return {
        node: lightMdToJsx(
          t('Moved {{ title }} from starting on {{ oldDate }} to {{ newDate }}', {
            title: toLogDisplayMd(task),
            oldDate,
            newDate
          })
        ),
        searchValue: t(
          'Moved {{ title }} from starting on {{ oldDate }} to {{ newDate }}',
          {
            title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' '),
            oldDate,
            newDate
          }
        )
      };
    }
    case ExperienceEventType.AUTOMATION_MOVED: {
      if (!automation) {
        return null;
      }
      const oldDate = formatMovedDate(d.prevStart);
      const newDate = formatMovedDate(d.nextStart);
      return {
        node: lightMdToJsx(
          t('Moved **{{ title }}** from running on {{ oldDate }} to {{ newDate }}', {
            title: automation.title,
            oldDate,
            newDate
          })
        ),
        searchValue: t(
          'Moved **{{ title }}** from running on {{ oldDate }} to {{ newDate }}',
          {
            title: automation.title,
            oldDate,
            newDate
          }
        )
      };
    }
    case ExperienceEventType.MESSAGE_RESENT:
      if (!message) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Resent {{ title }} via {{ channels }}', {
            title: toLogDisplayMd(message),
            channels: joinWithCommaAndAnd(
              compact(d.channels.map((c) => channelToString(c, t))),
              t
            )
          })
        ),
        searchValue: t('Resent {{ title }} via {{ channels }}', {
          title: [getItemDisplayName(message), message.adminName ? message.subject : ''].filter(Boolean).join(' '),
          channels: joinWithCommaAndAnd(
            compact(d.channels.map((c) => channelToString(c, t))),
            t
          )
        })
      };
    case ExperienceEventType.TASK_RESENT:
      if (!task) {
        return null;
      }
      return {
        node: lightMdToJsx(
          t('Resent {{ title }} via {{ channels }}', {
            title: toLogDisplayMd(task),
            channels: joinWithCommaAndAnd(
              compact(d.channels.map((c) => channelToString(c, t))),
              t
            )
          })
        ),
        searchValue: t('Resent {{ title }} via {{ channels }}', {
          title: [getItemDisplayName(task), task.adminName ? task.title : ''].filter(Boolean).join(' '),
          channels: joinWithCommaAndAnd(
            compact(d.channels.map((c) => channelToString(c, t))),
            t
          )
        })
      };
    case ExperienceEventType.DOCUSIGN_RESTARTED:
      const resource = data.resourcesById[d.resourceId];
      if (resource?.type !== TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE) {
        return null;
      }
      const actors = compact(
        resource.signers.map((s) => data.actorDescriptors[s.actor.key])
      ).map((x) => `**${x.label}**`);
      return {
        node: lightMdToJsx(
          t('Restarted signature process for {{ actors }} - **{{ doc }}**', {
            doc: resource.name,
            actors: joinWithCommaAndAnd(actors, t)
          })
        ),
        searchValue: t(
          'Restarted signature process for {{ actors }} - {{ doc }}',
          {
            doc: resource.name,
            actors: joinWithCommaAndAnd(actors, t)
          }
        )
      };
    default:
      if (ENVS.env.safe) {
        return assertNever(d);
      }
      console.log('Unknown event type', d);
      return null;
  }
};
